import { useCallback, useEffect, useRef, useState } from "react";

const getFsEl = () =>
  document.fullscreenElement ||
  document.webkitFullscreenElement ||
  document.mozFullScreenElement ||
  document.msFullscreenElement ||
  null;

const reqFs = async (el) => {
  if (!el) return;
  if (el.requestFullscreen) return el.requestFullscreen();
  if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen();
  if (el.mozRequestFullScreen) return el.mozRequestFullScreen();
  if (el.msRequestFullscreen) return el.msRequestFullscreen();
};

const exitFs = async () => {
  if (document.exitFullscreen) return document.exitFullscreen();
  if (document.webkitExitFullscreen) return document.webkitExitFullscreen();
  if (document.mozCancelFullScreen) return document.mozCancelFullScreen();
  if (document.msExitFullscreen) return document.msExitFullscreen();
};

export default function useFullScreen(targetRef) {
  const internalRef = useRef(null);
  const ref = targetRef ?? internalRef;
  const [isFullscreen, setIsFullscreen] = useState(() => !!getFsEl());

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!getFsEl());
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    document.addEventListener("mozfullscreenchange", onChange);
    document.addEventListener("MSFullscreenChange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
      document.removeEventListener("mozfullscreenchange", onChange);
      document.removeEventListener("MSFullscreenChange", onChange);
    };
  }, []);

  const enter = useCallback(
    () => reqFs(ref.current || document.documentElement),
    [ref]
  );
  const exit = useCallback(() => exitFs(), []);
  const toggle = useCallback(
    () =>
      getFsEl() ? exitFs() : reqFs(ref.current || document.documentElement),
    [ref]
  );

  return { ref, isFullscreen, enter, exit, toggle };
}
